import {Component, inject, input, output, signal, computed, ChangeDetectionStrategy} from "@angular/core"
import {PermissionsService} from "@app/common/services/permissions/permissions.service"
import {InputContainerComponent} from "@common/components/inputs/input-container/input-container.component"
import {NumericInputComponent} from "@common/components/inputs/numeric-input/numeric-input.component"
import {ToggleComponent} from "@common/components/buttons/toggle/toggle.component"
import {TransformMode, TransformValue, from3dsMaxTransform, to3dsMaxTransform, transformMatrixFromTranslationRotationScale} from "@cm/template-nodes"

import {Matrix4} from "@cm/math"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-transform-matrix-array",
    templateUrl: "./transform-matrix-array.component.html",
    styleUrl: "./transform-matrix-array.component.scss",
    imports: [InputContainerComponent, NumericInputComponent, ToggleComponent],
})
export class TransformMatrixArrayComponent {
    readonly $matrixArray = input.required<TransformValue>({alias: "matrixArray"})
    readonly matrixArrayChanged = output<TransformValue>()
    readonly $transformMode = input.required<TransformMode>({alias: "transformMode"})
    readonly transformModeChanged = output<TransformMode>()
    readonly $rotationReadonly = input(false, {alias: "rotationReadonly"})
    TransformMode = TransformMode

    private readonly permission = inject(PermissionsService)
    readonly $can = this.permission.$to
    readonly $useMaxCoordinates = signal(true)
    readonly $3dsMaxTransformOrientationFlag = input.required<boolean>({alias: "3dsMaxTransformOrientationFlag"})

    private readonly $transform = computed(() => {
        const transformMatrix = new Matrix4(this.$matrixArray())
        if (!this.$useMaxCoordinates()) {
            const {position, quaternion, scale} = transformMatrix.decompose()
            const rotation = quaternion.toEuler()
            return {position, rotation, scale}
        } else {
            return to3dsMaxTransform(transformMatrix, this.$3dsMaxTransformOrientationFlag())
        }
    })

    private getTransformValue(key: "x" | "y" | "z", transformMode: TransformMode.Rotate | TransformMode.Translate) {
        const transform = this.$transform()
        if (!transform) return 0.0

        const {position, rotation} = transform
        if (transformMode === TransformMode.Translate) return position[key]
        else if (transformMode === TransformMode.Rotate) return rotation[key] * (180.0 / Math.PI)
        else throw new Error("Unexpected transform mode")
    }

    private setTransformValue(key: "x" | "y" | "z", value: number, transformMode: TransformMode.Rotate | TransformMode.Translate) {
        const transform = this.$transform()
        if (!transform) return

        const {position, rotation, scale} = transform

        const positionCopy = position.copy()
        const rotationCopy = rotation.copy()

        if (transformMode === TransformMode.Translate) positionCopy[key] = value
        else if (transformMode === TransformMode.Rotate) rotationCopy[key] = value * (Math.PI / 180.0)
        else throw new Error("Unexpected transform mode")

        const transformMatrix = this.$useMaxCoordinates()
            ? from3dsMaxTransform(positionCopy, rotationCopy, scale, this.$3dsMaxTransformOrientationFlag())
            : transformMatrixFromTranslationRotationScale(positionCopy, rotationCopy, scale)

        this.matrixArrayChanged.emit(transformMatrix.toArray())
    }

    readonly $translateValueX = computed(() => this.getTransformValue("x", TransformMode.Translate))
    readonly $translateValueY = computed(() => this.getTransformValue("y", TransformMode.Translate))
    readonly $translateValueZ = computed(() => this.getTransformValue("z", TransformMode.Translate))
    readonly $rotationValueX = computed(() => this.getTransformValue("x", TransformMode.Rotate))
    readonly $rotationValueY = computed(() => this.getTransformValue("y", TransformMode.Rotate))
    readonly $rotationValueZ = computed(() => this.getTransformValue("z", TransformMode.Rotate))

    setTranslateValueX(value: number | null | undefined) {
        if (value != null) this.setTransformValue("x", value, TransformMode.Translate)
    }
    setTranslateValueY(value: number | null | undefined) {
        if (value != null) this.setTransformValue("y", value, TransformMode.Translate)
    }
    setTranslateValueZ(value: number | null | undefined) {
        if (value != null) this.setTransformValue("z", value, TransformMode.Translate)
    }
    setRotationValueX(value: number | null | undefined) {
        if (value != null) this.setTransformValue("x", value, TransformMode.Rotate)
    }
    setRotationValueY(value: number | null | undefined) {
        if (value != null) this.setTransformValue("y", value, TransformMode.Rotate)
    }
    setRotationValueZ(value: number | null | undefined) {
        if (value != null) this.setTransformValue("z", value, TransformMode.Rotate)
    }
}
